import { armFor } from "@healthos/heartbeat-rules";
import type { api } from "@healthos/schemas";
import { attentionTierLabel } from "@healthos/shared";
import {
  ArrowCounterClockwise,
  BellRinging,
  BellSimple,
  CaretDown,
  CheckCircle,
  CircleNotch,
  Flag,
  Heartbeat,
  Pill,
  PlayCircle,
  Sparkle,
  Target,
  WarningCircle,
} from "@phosphor-icons/react";
import type { useMutation } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useDemoConfirm } from "./demo-confirm";
import {
  DAY_OPTIONS,
  DEMO_CAPABILITIES,
  type DemoHeartbeatStoryId,
  type DemoMomentCardVariables,
  demoCapabilityKey,
  type LensFillVariables,
  type LifeContext,
  type PersonaId,
  personaMeta,
  type RouteContext,
  type Scenario,
  SIGNAL_PRESETS,
  type SignalPreset,
  type StepStatus,
  type WhoamiResponse,
} from "./demo-control-model";

/**
 * The carrier cards HealthOS actually surfaces on /today, each labelled with
 * the tier it renders so a treatment can be called up deliberately rather than
 * found by clicking. Adding a surface is one row here plus one case in the
 * server's story table, which is what keeps a button from reaching a story the
 * server cannot build. Reminder, reflection, reengagement, navigator and the
 * plan-item recipe card were retired with the plan: the server refuses them
 * with a 409, so no button here calls them.
 */

// One micro-caps label style for the groups inside a panel section. Extracted
// because it was hand-typed at each of its three call sites; the 8px is
// deliberately below the text-2xs floor (9px) for this dev-only chrome, so it
// is stated once rather than repeated.
const panelLabel = "font-mono text-[8px] uppercase tracking-[0.13em] text-gray-500";

export const buttonBase =
  "rounded-md border px-2 py-1.5 text-[10.5px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-55";
export const quietButton =
  "border-white/45 bg-white/55 text-gray-700 hover:border-teal-400/60 hover:bg-white/85";
const selectedButton = "border-teal-500/70 bg-white/90 text-navy shadow-sm";

function experienceDay(data: WhoamiResponse | undefined): number {
  return data?.demoExperienceDay ?? 1;
}

function experienceLabel(day: number): string {
  return `D${day}`;
}

export function PanelSection({
  title,
  eyebrow,
  defaultOpen = false,
  children,
}: {
  title: string;
  eyebrow?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  return (
    <details
      open={defaultOpen}
      className="group rounded-lg border border-white/45 bg-white/45 px-3 py-2.5"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
        <span className="min-w-0">
          {eyebrow ? (
            <span className="block font-mono text-[8.5px] uppercase tracking-[0.14em] text-gray-500">
              {eyebrow}
            </span>
          ) : null}
          <span className="block truncate font-semibold text-[12px] text-navy">{title}</span>
        </span>
        <CaretDown
          size={13}
          weight="bold"
          className="shrink-0 text-gray-400 transition-transform group-open:rotate-180"
        />
      </summary>
      <div className="mt-3">{children}</div>
    </details>
  );
}

export function DemoScriptSection({
  data,
  isBusy,
  seedMutation,
  resetMutation,
  dayZeroResetMutation,
  demoDayMutation,
  demoStoryMutation,
  lensFillMutation,
  signalMutation,
  ruleMutation,
}: {
  data: WhoamiResponse | undefined;
  isBusy: boolean;
  seedMutation: ReturnType<
    typeof useMutation<unknown, Error, { persona: PersonaId; lifeContext: LifeContext }>
  >;
  resetMutation: ReturnType<typeof useMutation<unknown, Error, void>>;
  dayZeroResetMutation: ReturnType<typeof useMutation<unknown, Error, void>>;
  demoDayMutation: ReturnType<typeof useMutation<api.DemoExperienceDayResponse, Error, number>>;
  demoStoryMutation: ReturnType<
    typeof useMutation<api.DemoHeartbeatStoryResponse, Error, DemoHeartbeatStoryId>
  >;
  lensFillMutation: ReturnType<
    typeof useMutation<api.DevFillLensResponse, Error, LensFillVariables>
  >;
  signalMutation: ReturnType<typeof useMutation<api.DemoSignalResponse, Error, SignalPreset>>;
  ruleMutation: ReturnType<
    typeof useMutation<api.DemoMomentCardResponse, Error, DemoMomentCardVariables>
  >;
}) {
  const { confirmDestructive } = useDemoConfirm();
  const currentDay = experienceDay(data);
  /*
   * The guided path is a GLP-1 script, and it was shown to everyone: a member
   * on Mental Health Foundations got "Stage GLP-1", "Unlock GLP-1" and "Log
   * dose", none of which mean anything on their lens. The dose controls now
   * follow the active persona and the title says whose demo this is.
   */
  const isGlp1 = data?.persona === "glp1_companion";
  const guidedTitle = data?.persona
    ? `Guided ${personaMeta(data.persona).short} demo`
    : "Guided demo";
  const maxDay = DAY_OPTIONS.at(-1) ?? 7;
  const atMaxDay = currentDay >= maxDay;
  const nextDay = Math.min(currentDay + 1, maxDay);

  return (
    <PanelSection title={guidedTitle} eyebrow="Narrative" defaultOpen>
      <p className={`mb-2 ${panelLabel}`}>Demo path</p>
      <div className="grid grid-cols-2 gap-1.5">
        <ScriptButton
          Icon={Flag}
          label="Start onboarding"
          detail="Full reset"
          disabled={isBusy}
          running={resetMutation.isPending}
          onClick={() => {
            confirmDestructive("Start onboarding", () => resetMutation.mutate());
          }}
        />
        <ScriptButton
          Icon={ArrowCounterClockwise}
          label="Replay Day Zero"
          detail="Keeps signals"
          disabled={isBusy}
          running={dayZeroResetMutation.isPending}
          onClick={() => dayZeroResetMutation.mutate()}
        />
        <ScriptButton
          Icon={Sparkle}
          label="Stage GLP-1"
          detail={isGlp1 ? "Scene reset" : "Switches persona"}
          disabled={isBusy}
          running={seedMutation.isPending && seedMutation.variables?.persona === "glp1_companion"}
          onClick={() => {
            confirmDestructive("Stage GLP-1", () =>
              seedMutation.mutate({ persona: "glp1_companion", lifeContext: "default" }),
            );
          }}
        />
        <ScriptButton
          Icon={PlayCircle}
          label={
            atMaxDay ? `At ${experienceLabel(currentDay)}` : `Finish ${experienceLabel(currentDay)}`
          }
          detail={atMaxDay ? "Demo signals cap at D7" : `Move to ${experienceLabel(nextDay)}`}
          disabled={isBusy || atMaxDay}
          running={demoDayMutation.isPending && demoDayMutation.variables === nextDay}
          onClick={() => demoDayMutation.mutate(nextDay)}
        />
        {isGlp1 ? (
          <ScriptButton
            Icon={Pill}
            label="Unlock GLP-1"
            detail="Dose widgets"
            disabled={isBusy}
            running={lensFillMutation.isPending && lensFillMutation.variables?.lens === "glp1"}
            onClick={() => {
              confirmDestructive("Unlock GLP-1", () => lensFillMutation.mutate({ lens: "glp1" }));
            }}
          />
        ) : null}
        {isGlp1 ? (
          <ScriptButton
            Icon={Target}
            label="Log dose"
            detail="Dose signal"
            disabled={isBusy}
            running={signalMutation.isPending && signalMutation.variables === "medication_dose"}
            onClick={() => signalMutation.mutate("medication_dose")}
          />
        ) : null}
      </div>
      <p className={`mt-3 mb-2 ${panelLabel}`}>Moments</p>
      <div className="grid grid-cols-2 gap-1.5">
        <ScriptButton
          Icon={Heartbeat}
          label="Quiet tick"
          detail="No surface"
          disabled={isBusy || !data?.userId}
          running={storyRunning(demoStoryMutation, "quiet_log")}
          onClick={() => demoStoryMutation.mutate("quiet_log")}
        />
        {DEMO_CAPABILITIES.map((entry) => (
          <ScriptButton
            key={demoCapabilityKey(entry)}
            Icon={entry.Icon}
            label={entry.label}
            detail={`${attentionTierLabel(armFor(entry.capability).tier)} · ${entry.detail}`}
            disabled={isBusy || !data?.userId}
            running={
              ruleMutation.isPending &&
              ruleMutation.variables !== undefined &&
              demoCapabilityKey(ruleMutation.variables) === demoCapabilityKey(entry)
            }
            onClick={() =>
              ruleMutation.mutate({
                capability: entry.capability,
                ...(entry.withEpisode ? { withEpisode: true } : {}),
              })
            }
          />
        ))}
      </div>
    </PanelSection>
  );
}

function storyRunning(
  mutation: ReturnType<
    typeof useMutation<api.DemoHeartbeatStoryResponse, Error, DemoHeartbeatStoryId>
  >,
  story: DemoHeartbeatStoryId,
): boolean {
  return mutation.isPending && mutation.variables === story;
}

function ScriptButton({
  Icon,
  label,
  detail,
  disabled,
  running,
  onClick,
}: {
  Icon: typeof Heartbeat;
  label: string;
  detail: string;
  disabled: boolean;
  running: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="group flex min-h-[54px] items-start gap-2 rounded-md border border-white/45 bg-white/60 px-2 py-2 text-left transition-colors hover:border-teal-400/60 hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-55"
    >
      {running ? (
        <CircleNotch
          size={15}
          weight="bold"
          className="mt-0.5 motion-safe:animate-spin text-teal-700"
        />
      ) : (
        <Icon
          size={15}
          weight="duotone"
          className="mt-0.5 shrink-0 text-gray-500 group-hover:text-teal-700"
        />
      )}
      <span className="min-w-0">
        <span className="block truncate font-semibold text-[11px] leading-tight text-navy">
          {running ? "Working..." : label}
        </span>
        <span className="mt-0.5 block truncate text-[9.5px] leading-tight text-gray-500">
          {detail}
        </span>
      </span>
    </button>
  );
}

export function DemoDaySection({
  data,
  isBusy,
  routeContext,
  demoDayMutation,
  tickMutation,
  briefMutation,
  testPushMutation,
  allPushTypesMutation,
  forceAttentionMutation,
}: {
  data: WhoamiResponse | undefined;
  isBusy: boolean;
  routeContext: RouteContext;
  demoDayMutation: ReturnType<typeof useMutation<api.DemoExperienceDayResponse, Error, number>>;
  tickMutation: ReturnType<typeof useMutation<api.SimulationProxyResponse, Error, boolean>>;
  briefMutation: ReturnType<typeof useMutation<unknown, Error, void>>;
  testPushMutation: ReturnType<typeof useMutation<unknown, Error, void>>;
  allPushTypesMutation: ReturnType<typeof useMutation<api.DevPushAllResponse, Error, void>>;
  forceAttentionMutation: ReturnType<typeof useMutation<api.SimulationProxyResponse, Error, void>>;
}) {
  const highlighted = routeContext === "patient";
  const currentExperienceDay = experienceDay(data);
  const pendingDay = demoDayMutation.variables;
  return (
    <PanelSection
      title="Timeline"
      eyebrow={highlighted ? "Patient path" : "Patient controls"}
      defaultOpen
    >
      <div className="grid grid-cols-7 gap-1">
        {DAY_OPTIONS.map((day) => {
          const isActive = currentExperienceDay === day;
          const isRunning = demoDayMutation.isPending && demoDayMutation.variables === day;
          return (
            <button
              key={day}
              type="button"
              disabled={isBusy}
              aria-pressed={isActive}
              onClick={() => demoDayMutation.mutate(day)}
              className={`grid h-8 min-w-0 place-items-center rounded-md border font-mono text-[10px] tabular-nums transition-colors ${
                isActive ? selectedButton : quietButton
              } disabled:cursor-not-allowed disabled:opacity-60`}
              title={`Force demo to ${experienceLabel(day)}`}
            >
              {isRunning ? "..." : `D${day}`}
            </button>
          );
        })}
      </div>

      {demoDayMutation.isPending && pendingDay != null ? (
        <p className="mt-2 font-mono text-[9px] text-gray-500">
          Preparing {experienceLabel(pendingDay)} signals...
        </p>
      ) : null}

      <div className="mt-2 grid grid-cols-2 gap-1.5">
        <ActionButton
          Icon={BellRinging}
          label={forceAttentionMutation.isPending ? "Forcing..." : "Force card"}
          disabled={isBusy || !data?.userId}
          running={forceAttentionMutation.isPending}
          tone="teal"
          onClick={() => forceAttentionMutation.mutate()}
        />
        <ActionButton
          Icon={Heartbeat}
          label={tickMutation.isPending ? "Ticking..." : "HB tick"}
          disabled={isBusy || !data?.userId}
          running={tickMutation.isPending}
          tone="teal"
          onClick={() => tickMutation.mutate(false)}
        />
        <ActionButton
          Icon={Sparkle}
          label={briefMutation.isPending ? "Generating..." : "Brief"}
          disabled={isBusy || !data?.userId}
          running={briefMutation.isPending}
          tone="purple"
          onClick={() => briefMutation.mutate()}
        />
        <ActionButton
          Icon={BellSimple}
          label={testPushMutation.isPending ? "Sending..." : "Push"}
          disabled={isBusy || !data?.userId}
          running={testPushMutation.isPending}
          tone="sky"
          onClick={() => testPushMutation.mutate()}
        />
        <ActionButton
          Icon={BellRinging}
          label={allPushTypesMutation.isPending ? "Sending all..." : "All push types"}
          disabled={isBusy || !data?.userId}
          running={allPushTypesMutation.isPending}
          tone="sky"
          wide
          onClick={() => allPushTypesMutation.mutate()}
        />
      </div>
    </PanelSection>
  );
}

function ActionButton({
  Icon,
  label,
  disabled,
  running,
  tone,
  wide = false,
  onClick,
}: {
  Icon: typeof Heartbeat;
  label: string;
  disabled: boolean;
  running: boolean;
  tone: "teal" | "purple" | "sky";
  wide?: boolean;
  onClick: () => void;
}) {
  const toneClass =
    tone === "purple"
      ? "border-purple-400/60 bg-purple-50/60 text-purple-800 hover:bg-purple-100/75"
      : tone === "sky"
        ? "border-sky-400/60 bg-sky-50/60 text-sky-800 hover:bg-sky-100/75"
        : "border-teal-500/60 bg-teal-50/70 text-teal-800 hover:bg-teal-100/80";
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex min-h-8 items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[10px] font-semibold transition-colors disabled:cursor-not-allowed disabled:border-mist-dark/40 disabled:bg-white/40 disabled:text-gray-400 ${toneClass} ${wide ? "col-span-2" : ""}`}
    >
      {running ? (
        <CircleNotch size={12} weight="bold" className="motion-safe:animate-spin" />
      ) : (
        <Icon size={12} weight="regular" className="shrink-0" />
      )}
      <span className="min-w-0 truncate">{label}</span>
    </button>
  );
}

export function SignalPaletteSection({
  isBusy,
  signalMutation,
}: {
  isBusy: boolean;
  signalMutation: ReturnType<typeof useMutation<api.DemoSignalResponse, Error, SignalPreset>>;
}) {
  return (
    <PanelSection title="Signal palette" eyebrow="Stage evidence">
      <div className="grid grid-cols-2 gap-1.5">
        {SIGNAL_PRESETS.map((preset) => (
          <SignalPresetButton
            key={preset.id}
            preset={preset}
            disabled={isBusy}
            running={signalMutation.isPending && signalMutation.variables === preset.id}
            onClick={() => signalMutation.mutate(preset.id)}
          />
        ))}
      </div>
    </PanelSection>
  );
}

function SignalPresetButton({
  preset,
  disabled,
  running,
  onClick,
}: {
  preset: (typeof SIGNAL_PRESETS)[number];
  disabled: boolean;
  running: boolean;
  onClick: () => void;
}) {
  const Icon = preset.Icon;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex min-h-8 items-center gap-1.5 text-left ${buttonBase} ${quietButton}`}
      title={preset.title}
    >
      {running ? (
        <CircleNotch size={12} weight="bold" className="motion-safe:animate-spin text-teal-700" />
      ) : (
        <Icon size={12} weight="regular" className="shrink-0 text-gray-500" />
      )}
      <span className="min-w-0 truncate">{running ? "Adding..." : preset.label}</span>
    </button>
  );
}

/**
 * Force a specific capture prompt ("log this now" CTA) onto /today, bypassing
 * eligibility, the LLM, and the time-of-day window. One button per template so
 * every prompt's card + tap-to-log flow can be reviewed for each persona.
 */

export function ScenarioSection({
  scenarios,
  isBusy,
  activeScenarioId,
  routeContext,
  onRun,
}: {
  scenarios: Scenario[];
  isBusy: boolean;
  activeScenarioId: string | null;
  routeContext: RouteContext;
  onRun: (scenario: Scenario) => void;
}) {
  const { confirmDestructive } = useDemoConfirm();
  return (
    <PanelSection title="Scene switches" eyebrow="One click scenes">
      <div className="grid grid-cols-3 gap-1.5">
        {scenarios
          .filter((s) => !s.devOnly || import.meta.env.DEV)
          .filter((s) => s.context === routeContext)
          .map((s) => (
            <ScenarioChip
              key={s.id}
              scenario={s}
              disabled={isBusy}
              running={activeScenarioId === s.id}
              onClick={() => {
                if (s.destructive === false) onRun(s);
                else confirmDestructive(s.label, () => onRun(s));
              }}
            />
          ))}
      </div>
      <details className="mt-2">
        <summary className="cursor-pointer font-mono text-[9.5px] text-gray-500 hover:text-navy">
          {routeContext === "patient" ? "Ops scenes" : "Member scenes"}
        </summary>
        <div className="mt-2 grid grid-cols-3 gap-1.5">
          {scenarios
            .filter((s) => !s.devOnly || import.meta.env.DEV)
            .filter((s) => s.context !== routeContext)
            .map((s) => (
              <ScenarioChip
                key={s.id}
                scenario={s}
                disabled={isBusy}
                running={activeScenarioId === s.id}
                onClick={() => {
                  if (s.destructive === false) onRun(s);
                  else confirmDestructive(s.label, () => onRun(s));
                }}
              />
            ))}
        </div>
      </details>
    </PanelSection>
  );
}

function ScenarioChip({
  scenario,
  disabled,
  running,
  onClick,
}: {
  scenario: Scenario;
  disabled: boolean;
  running: boolean;
  onClick: () => void;
}) {
  const Icon = scenario.Icon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={scenario.hint}
      className={`group relative flex min-h-[58px] flex-col items-start gap-1 rounded-md border px-2 py-2 text-left transition-colors ${
        running
          ? "border-teal-500/70 bg-teal-50/70 text-navy shadow-sm"
          : "border-white/40 bg-white/55 text-gray-700 hover:border-teal-400/60 hover:bg-white/85"
      } disabled:cursor-not-allowed disabled:opacity-60`}
    >
      <Icon
        size={14}
        weight={running ? "fill" : "regular"}
        className={running ? "text-teal-700" : "text-gray-500 group-hover:text-teal-600"}
      />
      <span className="font-medium text-[10.5px] leading-tight">{scenario.label}</span>
    </button>
  );
}

export function ProgressGlyph({ status }: { status: StepStatus }) {
  if (status === "done") return <CheckCircle size={11} weight="fill" className="text-leaf-dark" />;
  if (status === "running")
    return (
      <CircleNotch size={11} weight="bold" className="motion-safe:animate-spin text-teal-700" />
    );
  if (status === "error")
    return <WarningCircle size={11} weight="bold" className="text-rose-700" />;
  return <span className="inline-block h-2 w-2 rounded-full border border-gray-300" />;
}

export function progressColor(status: StepStatus): string {
  if (status === "done") return "text-leaf-dark";
  if (status === "error") return "text-rose-700";
  if (status === "running") return "text-navy";
  return "text-gray-400";
}

export function formatNowShowing(
  routeContext: RouteContext,
  pathname: string,
  whoami: WhoamiResponse | undefined,
  cohort: { headcount: number; pmpm: number },
): string {
  const route = pathname.length > 1 ? pathname : "/today";
  if (routeContext === "patient") {
    if (!whoami) return `${route} · loading identity...`;
    const persona = whoami.persona ? personaMeta(whoami.persona).demoName : "(no persona)";
    const personaLabel = whoami.persona ? personaMeta(whoami.persona).short : "-";
    const ctx = whoami.lifeContext === "low_agency" ? "Survival mode" : "Default";
    const day = whoami.demoExperienceDay ? ` · ${experienceLabel(whoami.demoExperienceDay)}` : "";
    return `${route} · ${persona} · ${personaLabel}${day} · ${ctx}`;
  }
  return `${route} · ${cohort.headcount.toLocaleString()} lives · $${cohort.pmpm} PMPM`;
}
